import React, { useCallback, useEffect, useState } from "react";
import { Step } from "../../../../../../models/step/step";
import { StepAddDialogComponent } from "../dialog/step-add-dialog/StepAddDialog.component";
import { StepUpdateDialogComponent } from "../dialog/step-update-dialog/StepUpdateDialog.component";
import { StepDeleteDialogComponent } from "../dialog/step-delete-dialog/StepDeleteDialog.component";
import { StepComponent } from "../step/Step.component";
import {
  Fab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Loader } from "../../../../../common/loader/Loader.component";
import "./Steps.component.sass";
import { move } from "../../../../../../lib/move";
import { useSnackbar } from "notistack";
import { getSnackbarOptions } from "../../../../../../lib/getSnackbarOptions";
import { useApi } from "../../../../../common/providers/Api.provider";
import { useTranslation } from "react-i18next";

export interface StepsComponentProps {
  questionnaireId: string | undefined;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  onHasUnsavedQuestionChangesChange?: (hasUnsavedChanges: boolean) => void;
}

export function StepsComponent({
  questionnaireId,
  setHasError,
  onHasUnsavedQuestionChangesChange,
}: StepsComponentProps) {
  const { t: questionnaireTranslations } = useTranslation(
    "questionnaire-page",
    {
      keyPrefix: "steps",
      useSuspense: false,
    }
  );
  const { t: commonTranslations } = useTranslation("common", {
    useSuspense: false,
  });

  const api = useApi();
  const snackbar = useSnackbar();

  const [isStepsLoadingState, setIsStepsLoadingState] =
    useState<boolean>(false);
  const [stepsState, setStepsState] = useState<Step[]>([]);

  const [isStepAddDialogOpenState, setIsStepAddDialogOpenState] =
    useState<boolean>(false);

  const [stepToUpdateState, setStepToUpdateState] = useState<Step | undefined>(
    undefined
  );

  const [stepToDeleteState, setStepToDeleteState] = useState<Step | undefined>(
    undefined
  );

  const [activeStep, setActiveStep] = useState<Step | null>(null);
  const [dirtyStepsState, setDirtyStepsState] = useState<Record<number, boolean>>(
    {}
  );
  const [isUnsavedChangesDialogOpenState, setIsUnsavedChangesDialogOpenState] =
    useState<boolean>(false);
  const [pendingActiveStepState, setPendingActiveStepState] = useState<
    Step | null | undefined
  >(undefined);

  const handleDirtyStepChange = useCallback((stepId: number, isDirty: boolean) => {
    setDirtyStepsState((prevState) => {
      if (prevState[stepId] === isDirty) {
        return prevState;
      }

      return {
        ...prevState,
        [stepId]: isDirty,
      };
    });
  }, []);

  const requestActiveStepChange = useCallback(
    (nextStep: Step | null): boolean => {
      if (activeStep && (!nextStep || nextStep.id !== activeStep.id)) {
        const hasUnsavedChanges = !!dirtyStepsState[activeStep.id];

        if (hasUnsavedChanges) {
          setPendingActiveStepState(nextStep);
          setIsUnsavedChangesDialogOpenState(true);
          return false;
        }
      }

      setActiveStep(nextStep);
      return true;
    },
    [activeStep, dirtyStepsState]
  );

  const closeUnsavedChangesDialog = () => {
    setIsUnsavedChangesDialogOpenState(false);
    setPendingActiveStepState(undefined);
  };

  const continueWithoutSaving = () => {
    setActiveStep(pendingActiveStepState ?? null);
    closeUnsavedChangesDialog();
  };

  useEffect(() => {
    if (onHasUnsavedQuestionChangesChange) {
      onHasUnsavedQuestionChangesChange(
        Object.values(dirtyStepsState).some(Boolean)
      );
    }
  }, [dirtyStepsState, onHasUnsavedQuestionChangesChange]);

  useEffect(() => {
    const stepIds = new Set(stepsState.map((step) => step.id));

    setDirtyStepsState((prevState) => {
      let hasChanges = false;
      const nextState: Record<number, boolean> = {};

      Object.keys(prevState).forEach((stepId) => {
        const parsedStepId = Number(stepId);

        if (stepIds.has(parsedStepId)) {
          nextState[parsedStepId] = prevState[parsedStepId];
        } else {
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : prevState;
    });
  }, [stepsState]);

  const getSteps = async (questionnaireId: number) => {
    try {
      setIsStepsLoadingState(true);
      const response = await api.stepsByQuestionnaireId(questionnaireId);

      setStepsState(response.data);
    } catch (e) {
      console.error(e);
      setHasError(true);
    } finally {
      setIsStepsLoadingState(false);
    }
  };

  const handleCloseStepAddDialog = (result?: Step) => {
    if (!!result) {
      setStepsState([...stepsState, result]);
    }

    setIsStepAddDialogOpenState(false);
  };

  const handleCloseStepUpdateDialog = (result?: Step) => {
    if (!!result) {
      setStepsState(
        stepsState.map((s) => (s.id === result.id ? { ...s, ...result } : s))
      );
    }

    setStepToUpdateState(undefined);
  };

  const handleCloseStepDeleteDialog = (result?: number) => {
    if (!!result) {
      setStepsState(stepsState.filter((s) => s.id !== result));
    }

    setStepToDeleteState(undefined);
  };

  const handleSendStepInDirection = async (
    event: React.ChangeEvent<{}>,
    index: number,
    direction: "upward" | "downward"
  ) => {
    event.stopPropagation();
    if (!requestActiveStepChange(null)) {
      return;
    }

    const newIndex = direction === "upward" ? index - 1 : index + 1;
    let steps = stepsState;

    if (
      (direction === "upward" && newIndex >= 0) ||
      (direction === "downward" && newIndex <= steps.length - 1)
    ) {
      const response = await api.stepsMove(steps[index].id, {
        order: newIndex,
      });

      if (response.success) {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({
            variant: "success",
          })
        );

        steps = move(steps, index, newIndex).map((s, i) => ({
          ...s,
          order: i,
        }));

        setStepsState(steps);
      }
    }
  };

  useEffect(() => {
    if (!!questionnaireId) {
      getSteps(Number(questionnaireId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireId]);

  return (
    <>
      <Dialog open={isUnsavedChangesDialogOpenState}>
        <DialogTitle>
          {questionnaireTranslations("unsavedChangesDialog.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {questionnaireTranslations("unsavedChangesDialog.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUnsavedChangesDialog} color="secondary">
            {questionnaireTranslations("unsavedChangesDialog.stay")}
          </Button>
          <Button onClick={continueWithoutSaving} color="primary">
            {questionnaireTranslations(
              "unsavedChangesDialog.continueWithoutSaving"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <StepAddDialogComponent
        open={isStepAddDialogOpenState}
        handleClose={handleCloseStepAddDialog}
        questionnaireId={Number(questionnaireId)}
      />
      <StepUpdateDialogComponent
        open={!!stepToUpdateState}
        step={stepToUpdateState}
        handleClose={handleCloseStepUpdateDialog}
      />
      <StepDeleteDialogComponent
        open={!!stepToDeleteState}
        step={stepToDeleteState}
        handleClose={handleCloseStepDeleteDialog}
      />
      <div className="steps-container">
        {isStepsLoadingState ? (
          <Paper className="steps-container__paper">
            <Loader loadingText={commonTranslations("loading")} />
          </Paper>
        ) : (
          <>
            <div className="steps-container__steps">
              {stepsState.map((step: Step, index: number) => (
                <StepComponent
                  key={index}
                  step={step}
                  activeStep={activeStep}
                  requestActiveStepChange={requestActiveStepChange}
                  onDirtyChange={handleDirtyStepChange}
                  setHasError={setHasError}
                  setStepToUpdate={setStepToUpdateState}
                  setStepToDelete={setStepToDeleteState}
                  sendValueUpward={(event: React.ChangeEvent<{}>) =>
                    handleSendStepInDirection(event, index, "upward")
                  }
                  sendValueDownward={(event: React.ChangeEvent<{}>) =>
                    handleSendStepInDirection(event, index, "downward")
                  }
                  isUpwardDisabled={index === 0}
                  isDownwardDisabled={index === stepsState.length - 1}
                  setStepMaxScore={(score) => {
                    if (score) {
                      setStepsState(
                        stepsState.map((s) =>
                          s.id === step.id ? { ...s, maxScore: score } : s
                        )
                      );
                    }
                  }}
                />
              ))}
            </div>
            <div className="steps-container__fab-container">
              <Fab
                color="secondary"
                aria-label={questionnaireTranslations("addStep")}
                onClick={() => setIsStepAddDialogOpenState(true)}
              >
                <AddIcon />
              </Fab>
            </div>
          </>
        )}
      </div>
    </>
  );
}
